import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogFooter, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { UserService } from "@/lib/db";
import { useAuth } from "@/contexts/AuthContext";
import { updateProfile } from "firebase/auth";
import { CheckCircle2, ChevronRight, User, Target, Settings, ArrowLeft } from "lucide-react";

interface OnboardingModalProps {
    open: boolean;
    onComplete: () => void;
}

const STEPS = [
    { id: 1, title: "Identity", icon: User, desc: "Tell us about yourself" },
    { id: 2, title: "Goals", icon: Target, desc: "What are you looking for?" },
    { id: 3, title: "Preferences", icon: Settings, desc: "Fine-tune your job search" }
];

export function OnboardingModal({ open, onComplete }: OnboardingModalProps) {
    const { user } = useAuth();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);

    // Form State
    const [name, setName] = useState("");
    const [experience, setExperience] = useState(""); // years
    const [targetRole, setTargetRole] = useState("");
    const [industry, setIndustry] = useState("");
    const [status, setStatus] = useState("active"); // active, passive
    const [targetSalary, setTargetSalary] = useState("");
    const [targetCurrency, setTargetCurrency] = useState("USD");
    const [workMode, setWorkMode] = useState("remote"); // remote, hybrid, onsite

    useEffect(() => {
        if (user?.displayName) setName(user.displayName);
    }, [user]);

    const handleNext = () => setStep(s => Math.min(s + 1, 3));
    const handleBack = () => setStep(s => Math.max(s - 1, 1));

    const handleSubmit = async () => {
        if (!user) return;
        setLoading(true);
        try {
            if (name !== user.displayName) await updateProfile(user, { displayName: name });

            await UserService.updateProfile(user.uid, {
                displayName: name,
                yearsOfExperience: experience,
                targetRole,
                targetCompany: industry, // mapping to existing field for now, or new one
                jobSearchStatus: status,
                targetSalary,
                targetCurrency,
                workMode,
                onboarded: true
            });
            onComplete();
        } catch (error) {
            console.error("Onboarding failed", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open}>
            <DialogContent className="sm:max-w-[500px] h-[90vh] sm:h-[550px] flex flex-col [&>button]:hidden" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
                {/* Header with Progress Steps */}
                <div className="mb-6">
                    <div className="flex justify-between items-center relative mb-8 px-4 mt-2">
                        {/* Connecting Line */}
                        <div className="absolute left-0 right-0 top-1/2 h-0.5 bg-muted -z-10" />

                        {STEPS.map((s) => {
                            const isCompleted = step > s.id;
                            const isCurrent = step === s.id;
                            return (
                                <div key={s.id} className="flex flex-col items-center gap-2 bg-background px-2">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors ${isCompleted || isCurrent ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/30 bg-background text-muted-foreground"
                                        }`}>
                                        {isCompleted ? <CheckCircle2 className="w-5 h-5" /> : <s.icon className="w-4 h-4" />}
                                    </div>
                                    <span className={`text-[10px] font-medium uppercase tracking-wider ${isCurrent ? "text-primary" : "text-muted-foreground"}`}>{s.title}</span>
                                </div>
                            );
                        })}
                    </div>
                    <DialogTitle className="text-xl text-center">{STEPS[step - 1].title}</DialogTitle>
                    <DialogDescription className="text-center">{STEPS[step - 1].desc}</DialogDescription>
                </div>

                {/* Form Content */}
                <div className="flex-1 space-y-6 px-1 overflow-y-auto py-2">
                    {step === 1 && (
                        <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                            <div className="grid gap-2">
                                <Label htmlFor="name">Full Name</Label>
                                <Input id="name" value={name} onChange={e => setName(e.target.value)} placeholder="John Doe" />
                            </div>
                            <div className="grid gap-2">
                                <Label htmlFor="exp">Years of Experience</Label>
                                <Input id="exp" value={experience} onChange={e => setExperience(e.target.value)} type="number" placeholder="e.g. 5" />
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                            <div className="grid gap-2">
                                <Label htmlFor="role">Target Role</Label>
                                <Input id="role" value={targetRole} onChange={e => setTargetRole(e.target.value)} placeholder="Senior Product Manager" />
                            </div>
                            <div className="grid gap-2">
                                <Label htmlFor="industry">Preferred Industry</Label>
                                <Input id="industry" value={industry} onChange={e => setIndustry(e.target.value)} placeholder="Fintech, Healthtech..." />
                            </div>
                            <div className="grid gap-2">
                                <Label>Current Status</Label>
                                <RadioGroup value={status} onValueChange={setStatus} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="flex items-center space-x-2 border p-3 rounded-md cursor-pointer hover:bg-accent has-[:checked]:bg-accent/50 has-[:checked]:border-primary transition-all">
                                        <RadioGroupItem value="active" id="active" />
                                        <Label htmlFor="active" className="cursor-pointer flex-1">Actively Looking</Label>
                                    </div>
                                    <div className="flex items-center space-x-2 border p-3 rounded-md cursor-pointer hover:bg-accent has-[:checked]:bg-accent/50 has-[:checked]:border-primary transition-all">
                                        <RadioGroupItem value="passive" id="passive" />
                                        <Label htmlFor="passive" className="cursor-pointer flex-1">Open to Offers</Label>
                                    </div>
                                </RadioGroup>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                            <div className="grid gap-2">
                                <Label htmlFor="salary">Target Salary</Label>
                                <div className="flex gap-2">
                                    <div className="w-24 flex-shrink-0">
                                        <Select value={targetCurrency} onValueChange={setTargetCurrency}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="USD" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="USD">USD ($)</SelectItem>
                                                <SelectItem value="EUR">EUR (€)</SelectItem>
                                                <SelectItem value="GBP">GBP (£)</SelectItem>
                                                <SelectItem value="CAD">CAD ($)</SelectItem>
                                                <SelectItem value="AUD">AUD ($)</SelectItem>
                                                <SelectItem value="INR">INR (₹)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <Input id="salary" value={targetSalary} onChange={e => setTargetSalary(e.target.value)} placeholder="e.g. 120,000" className="flex-1" />
                                </div>
                            </div>
                            <div className="grid gap-2">
                                <Label>Work Preference</Label>
                                <Select value={workMode} onValueChange={setWorkMode}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select preference" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="remote">Remote Only</SelectItem>
                                        <SelectItem value="hybrid">Hybrid</SelectItem>
                                        <SelectItem value="onsite">On-site</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter className="flex justify-between mt-auto pt-6 border-t">
                    <Button variant="ghost" onClick={handleBack} disabled={step === 1} className={step === 1 ? "invisible" : ""}>
                        <ArrowLeft className="w-4 h-4 mr-2" /> Back
                    </Button>

                    {step < 3 ? (
                        <Button onClick={handleNext}>
                            Next <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                    ) : (
                        <Button onClick={handleSubmit} disabled={loading}>
                            {loading ? "Completing..." : "Complete Setup"}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
